import { CloudArrowDownIcon } from '@heroicons/react/24/outline';
import { ColumnDef, Row } from '@tanstack/react-table';

import { IconButton } from '@/components/core/IconButton';
import { StatusColumn } from '@/components/shared/StatusColumn';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { formatDateToMedium, toPixelColumnSize } from '@/services/utils';

import { ExportDataType } from './export.types';

export const isExportValid = (row: Row<ExportDataType>) => {
    return row.getValue('status') === 'FINISHED';
};

export const columns: (
    t: (key: string) => string,
) => ColumnDef<ExportDataType>[] = (t) => [
    {
        accessorKey: 'progress',
        header: t('UPLOAD.PROGRESS'),
        size: toPixelColumnSize(0.08),
        accessorFn: (row) => {
            if (row.total === 0) {
                return 0;
            }
            if (row.status === 'EVALUATING') {
                return 1;
            }
            let progress;
            if (row.processedCount === 0) {
                progress = Math.floor(90 * (row.count / row.total));
            } else {
                progress = Math.floor(
                    80 * (row.processedCount / row.total) +
                        10 * (row.count / row.total),
                );
            }
            if (row.status !== 'QUEUED' && row.status !== 'EVALUATING') {
                progress += 10;
            }
            return progress + '%';
        },
    },
    {
        accessorKey: 'total',
        header: t('TOTAL'),
        size: toPixelColumnSize(0.08),
    },
    {
        accessorKey: 'status',
        header: t('STATUS'),
        cell: ({ row }) => <StatusColumn status={row.getValue('status')} message={row.original.message} />,
        size: toPixelColumnSize(0.15),
    },

    {
        accessorKey: 'format',
        header: t('FORMAT'),
    },
    {
        accessorKey: 'creationDate',
        header: t('PUBLICATIONS.START'),
        cell: ({ row }) => (
            <div className="tw-whitespace-nowrap">
                {formatDateToMedium(new Date(row.getValue('creationDate')))}
            </div>
        ),
    },
    {
        accessorKey: 'finishedDate',
        header: t('PUBLICATIONS.END'),
        cell: ({ row }) => {

            const finishedDateTimestamp = row.getValue('finishedDate') as string;

            if (!finishedDateTimestamp) {
                return null;
            } else {
                return (
                    <div className="tw-whitespace-nowrap">
                        {formatDateToMedium(new Date(finishedDateTimestamp))}
                    </div>
                );
            }
        },
    },
    {
        accessorKey: 'creationUser',
        header: t('USER'),
        cell: ({ row }) => {
            // FIXME: AngularJS leave me alone
            const angularJsInjector = getInjector();
            const $rootScope = getRootScope(angularJsInjector);

            return (
                <label>
                    {$rootScope.getUserName(row.getValue('creationUser'))}
                </label>
            );
        },
    },
    {
        accessorKey: 'contentType',
        header: t('MESSAGING.CONTENT_TYPE'),
    },
    {
        accessorKey: 'actions',
        header: t('ACTIONS'),
        meta: {
            pinned: true
        },
        size: toPixelColumnSize(0.08),
        enableResizing: false,
        cell: ({ row }) => (
            <div className="tw-flex tw-flex-row-reverse">
                {isExportValid(row) && (
                    <div id={`row${row.id}-actions`}>
                        <a
                            href={`/api/v1/gatherings/${row.original.id}${row.original.digitalAssetPath}?download=true`}
                            target="_self"
                            data-testid="download-export"
                        >
                            <IconButton icon={CloudArrowDownIcon} size="md" title={t('DOWNLOAD_LABEL')}  data-testid="cloud-arrow-down"/>
                        </a>
                    </div>
                )}
            </div>
        ),
    },
];
